import React from 'react';
import { DemoContainer } from '@mui/x-date-pickers/internals/demo';
import moment, { Moment } from 'moment';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { TimeField } from '@mui/x-date-pickers/TimeField';
import { useStore } from '@cobuildlab/react-simple-state';
import { changeStrategyStore } from '../../strategy/strategy-events';
import AddIcon from '@mui/icons-material/Add';
import { Box, Grid, IconButton, Chip } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { makeStyles } from '@mui/styles';
import { StrategiesDocument, StrategiesUpdateInput, useUpdateStrategyMutation } from '../../../shared/types/generated';
import { openSnackbarAction } from '../../../shared/components/snackbar/snackbar-actions';
import { TypeMessage } from '../../../shared/components/snackbar/snackbar-types';

const useStyles = makeStyles(() => ({
  root: {
    '& > .MuiStack-root': {
      paddingTop: 0,
      flexDirection: 'row',
      '& > .MuiFormControl-root': {
        marginTop: 0,
        marginRight: 10,
      },
    },
  },
}));

type StrategyTimeProps = {
  onSuccess: () => void
}

export const StrategyTime: React.FC<StrategyTimeProps> = (
  {
    onSuccess,
  }) => {
  const classes = useStyles();
  const [startDate, setStartDate] = React.useState<Moment | null>(moment());
  const [endDate, setEndDate] = React.useState<Moment | null>(moment());
  const { strategy } = useStore(changeStrategyStore);

  const [callUpdate] = useUpdateStrategyMutation({
    awaitRefetchQueries: true,
    refetchQueries: [StrategiesDocument],
    onCompleted: () => {
      openSnackbarAction('Update strategy time successfully!', TypeMessage.SUCCESS);
      onSuccess();
    },
  });

  const time = (strategy?.time || []) as string[][];

  return (
    <Grid container>
      <Grid item xs={12}>
        <Grid container>
          <Grid item xs={12} md={3} sx={{ marginBottom: { xs: 1, md: 0 } }}>
            <Box display='flex' height='100%' alignItems='center'>
              <Box className={classes.root}>
                <LocalizationProvider dateAdapter={AdapterMoment}>
                  <DemoContainer components={['TimeField', 'TimeField']}>
                    <TimeField
                      defaultValue={startDate}
                      onChange={(newValue) => setStartDate(newValue)}
                      size='small'
                      style={{ width: 100, minWidth: 100 }}
                    />
                    <TimeField
                      value={endDate}
                      onChange={(newValue) => setEndDate(newValue)}
                      size='small'
                      style={{ width: 100, minWidth: 100 }}
                    />
                  </DemoContainer>
                </LocalizationProvider>
              </Box>
              <Box
                display='flex'
                height='100%'
                justifyContent='center'
                alignItems='center'
                component='span'
              >
                <IconButton
                  onClick={() => {
                    const data: StrategiesUpdateInput = {
                      time: [...time, [startDate?.format('hh:mm A'), endDate?.format('hh:mm A')]],
                    };
                    callUpdate({
                      variables: {
                        data,
                        where: {
                          id: strategy?.id,
                        },
                      },
                    });
                  }}
                >
                  <AddIcon sx={{ color: 'rgb(32, 125, 50)' }} />
                </IconButton>
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} md={9}>
            <Box overflow='auto' display='flex' height='100%' alignItems='center'>
              <Box display='flex' sx={{ padding: { xs: '10px 0', md: 0 } }} pl={1}>
                {time?.map((t, index) => {
                  const [a, b] = t;
                  return (
                    <Box mr={1}>
                      <Chip
                        variant='outlined'
                        label={`${a} - ${b}`}
                        deleteIcon={
                          <CloseIcon sx={{ color: '#000!important' }} />
                        }
                        onDelete={() => {
                          const currentTimes = [...time];
                          currentTimes.splice(index, 1);

                          const data: StrategiesUpdateInput = {
                            time: currentTimes,
                          };
                          callUpdate({
                            variables: {
                              data,
                              where: {
                                id: strategy?.id,
                              },
                            },
                          });
                        }}
                      />
                    </Box>
                  );
                })}
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};
